import type { TFunction } from 'i18next';

/**
 * In-app notifications are stored as already-rendered English text (the backend's
 * IN_APP_MESSAGES in app/communication/constants.py, formatted at send time), with
 * no copy of the values that went into them. To show them in the reader's language
 * we match the stored text against the English template for its `type`, pull the
 * placeholder values back out, and render `notifications.messages.<TYPE>` instead.
 *
 * Keep this map in step with IN_APP_MESSAGES. A message that no longer matches its
 * template (copy changed, unknown type) is shown as stored, never dropped.
 */
const EN_TEMPLATES: Record<string, string> = {
    INACTIVITY_REMINDER: "You haven't trained in a while. Come back and continue your progress!",
    REENGAGEMENT_REMINDER: "It's been a long break! Jump back into your training!",
    WELCOME_BACK: 'Welcome back! Great to see you training again!',
    FIRST_MODULE_SESSION: 'Nice! You just completed your first session with {teaching_method_name}!',
    SESSION_FEEDBACK_AVAILABLE: 'Your session feedback is ready to view.',
    STREAK_STARTED: '🔥 Your streak count starts today! Keep training daily to build it up.',
    STREAK_7_UNLOCKED: '🔥 You reached a 7 day training streak!',
    STREAK_14_UNLOCKED: '🔥 Amazing! You hit a 14 day training streak!',
    STREAK_30_UNLOCKED: '🏆 Incredible! You hit a 30 day training streak!',
    NEW_TEACHING_MODULE: "🎓 A new teaching module '{teaching_method_name}' has been added!",
    NEW_SCENARIO_ADDED: "🎭 A new scenario '{scenario_name}' is now available!",
    SESSIONS_10_COMPLETED: "🎯 Milestone reached! You've completed 10 training sessions!",
    SESSIONS_25_COMPLETED: "🌟 Amazing progress! You've completed 25 training sessions!",
    SESSIONS_50_COMPLETED: "🏆 Outstanding achievement! You've completed 50 training sessions!",
    APP_FEEDBACK_SUBMITTED: 'Your feedback has been submitted successfully. Thank you for helping us improve!',
    APP_FEEDBACK_UPDATED: 'Your feedback submission was updated — status is now {status_label}.',
    TRIAL_EXPIRY_REMINDER: '⏰ Your trial expires in {days_remaining} day(s). Upgrade now to keep practicing!',
    PAYMENT_SUCCESS: '🎉 Payment confirmed! Your {plan_label} is now active until {access_end_date_str}.',
    SEATS_RECONCILIATION_NEEDED:
        'Your new plan has {teacher_seats} teacher seat(s) for {teacher_count} teacher(s). '
        + 'Choose who keeps a seat on your Profile page.',
    COMPETITION_ANNOUNCEMENT: "🏆 A new competition '{competition_name}' has been scheduled!",
    COMPETITION_REMINDER: "⏰ '{competition_name}' starts in 24 hours. Get ready!",
    COMPETITION_WINNER: "🏆 You placed #{rank} in '{competition_name}' with {points_earned} pts!",
    REVIEW_REQUESTED: '📝 A human review was requested for a {module_name} session.',
    REVIEWER_ASSIGNED: "👀 You've been assigned to review a {module_name} session.",
    REVIEW_COMPLETED: '✅ Your {module_name} session review is complete.',
    SESSION_UNDER_REVIEW: '🔍 Your {module_name} session is being reviewed by a coach.',
    SURVEY_INVITATION: "📋 '{survey_title}' is open at {org_name}. Taking part is optional.",
    JOIN_REQUEST_RECEIVED: '🔔 {applicant_email} has asked to join {target_org_name}. Review it before {expiry_date}.',
    JOIN_REQUEST_PENDING_REVIEW: '🔔 A request from {applicant_email} to join {target_org_name} is waiting for a decision.',
    JOIN_REQUEST_SUBMITTED: "✅ Your request to join {target_org_name} was sent. You'll hear back by {expiry_date}.",
    JOIN_REQUEST_REMINDER: "⏰ {applicant_email}'s request to join {target_org_name} expires tomorrow, {expiry_date}.",
    JOIN_REQUEST_APPROVED: '🎉 Your request to join {target_org_name} was approved.',
    JOIN_REQUEST_REJECTED:
        "Your request to join {target_org_name} wasn't approved. You can try again from {cooldown_end_date}.",
    JOIN_REQUEST_LAPSED:
        'Your request to join {target_org_name} expired without a response. You can try again from {cooldown_end_date}.',
    JOIN_REQUEST_SEATED: "🎉 You're in — you now have full access to {target_org_name}.",
    JOIN_REQUEST_SEAT_BLOCKED:
        "⚠️ {applicant_email} is locked out of {target_org_name} — their trial ended and no seat is free.",
    JOIN_REQUEST_LOCKED_OUT:
        'Your trial ended and no seat at {target_org_name} is free yet. Their admin has been notified.',
    JOIN_REQUEST_AUTO_JOINED: '👋 {applicant_email} joined {target_org_name} automatically (auto-approve is on).',
    CLASS_ASSIGNMENT_PUBLISHED: "📚 New practice in {class_name}: '{assignment_title}'.",
    CLASS_GRADE_RELEASED: "🎓 Your grade for '{assignment_title}' is ready.",
};

// Fixed English labels the backend substitutes into a few templates.
const VALUE_KEYS: Record<string, Record<string, string>> = {
    plan_label: {
        'Monthly Plan': 'notifications.values.monthlyPlan',
        'Annual Plan': 'notifications.values.annualPlan',
    },
    status_label: {
        Pending: 'notifications.values.statusPending',
        'In Progress': 'notifications.values.statusInProgress',
        Resolved: 'notifications.values.statusResolved',
        Closed: 'notifications.values.statusClosed',
    },
};

const DATE_FIELDS = new Set(['access_end_date_str', 'expiry_date', 'cooldown_end_date']);

const escapeRegex = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const compiled: Record<string, { re: RegExp; names: string[] }> = {};

const compile = (type: string): { re: RegExp; names: string[] } | null => {
    const template = EN_TEMPLATES[type];
    if (!template) return null;
    if (!compiled[type]) {
        const names: string[] = [];
        const pattern = template
            .split(/(\{[a-z_]+\})/)
            .map((part) => {
                const m = /^\{([a-z_]+)\}$/.exec(part);
                if (!m) return escapeRegex(part);
                names.push(m[1]);
                return '([\\s\\S]+?)';
            })
            .join('');
        compiled[type] = { re: new RegExp(`^${pattern}$`), names };
    }
    return compiled[type];
};

// The backend writes dates as "October 20, 2026" or "Feb 7, 2026"; re-render them in
// the reader's locale. Anything that doesn't parse cleanly is left as written.
const localizeDate = (value: string, locale: string): string => {
    if (!/^[A-Za-z]+ \d{1,2}, \d{4}$/.test(value)) return value;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
};

export const translateNotificationMessage = (
    type: string,
    message: string,
    t: TFunction,
    locale: string,
): string => {
    if (locale.startsWith('en')) return message;
    const entry = compile(type);
    if (!entry) return message;
    const match = entry.re.exec(message);
    if (!match) return message;

    const values: Record<string, string | number> = {};
    entry.names.forEach((name, i) => {
        let value = match[i + 1];
        const labelKey = VALUE_KEYS[name]?.[value];
        if (labelKey) value = t(labelKey, { defaultValue: value });
        else if (DATE_FIELDS.has(name)) value = localizeDate(value, locale);
        values[name] = value;
    });

    return t(`notifications.messages.${type}`, { ...values, defaultValue: message });
};
